import type { AssistantAction } from '@secretario/shared';
import { useEffect, useState } from 'react';
import { getContactDetail } from '../../lib/api/personal.js';
import { IS_FULL } from '../../lib/edition.js';
import { openWhatsApp, signWhatsApp, whatsappDigits } from '../../lib/whatsapp.js';
import { markDraftHandled, sendDraftMessage } from '../../lib/assistantDraft.js';
import { inviteLink, inviteText } from '../../lib/invite.js';
import { recordInvite } from '../../lib/api/invites.js';
import { hapticSuccess, hapticTap, hapticWarn, shareText } from '../../lib/native.js';
import { useSession } from '../../lib/session.js';
import { useProfile } from '../../lib/useProfile.js';
import { track } from '../../lib/track.js';
import './assistant-modal.css';

type Draft = Extract<AssistantAction, { kind: 'send_message' }>;

/**
 * Rascunho de mensagem preparado pelo Secretário: quem recebe, o texto como vai
 * chegar e a decisão do dono (enviar, ajustar ou não enviar). Nada sai antes do
 * toque em Enviar.
 */
export function DraftCard({
  activityId,
  draft,
  onSent,
  onDismiss,
}: {
  activityId: string;
  draft: Draft;
  onSent: (roomId: string) => void;
  onDismiss: () => void;
}) {
  const session = useSession();
  const profile = useProfile().data;
  const [text, setText] = useState(draft.text);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const initial = draft.contactName.trim().charAt(0).toUpperCase() || '•';
  // Telefone do contato, para abrir a mensagem pronta no WhatsApp (o número não
  // viaja no rascunho: é lido aqui, do próprio contato).
  const [digits, setDigits] = useState<string | null>(null);
  const [handed, setHanded] = useState(false);
  // Sec Full: a mensagem foi enviada de verdade pelo WhatsApp.
  const [waSent, setWaSent] = useState(false);
  useEffect(() => {
    if (draft.whatsapp === false) return;
    let alive = true;
    getContactDetail(draft.contactId)
      .then((detail) => {
        if (alive) setDigits(whatsappDigits(detail.contact.phone));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [draft.contactId, draft.whatsapp]);

  async function send() {
    const body = text.trim();
    if (!draft.username || !body || busy) return;
    setBusy(true);
    setError('');
    hapticTap();
    try {
      const roomId = await sendDraftMessage(draft.username, body);
      markDraftHandled(activityId);
      hapticSuccess();
      onSent(roomId);
    } catch {
      setError('Não consegui enviar agora. Tenta de novo?');
      hapticWarn();
      setBusy(false);
    }
  }

  async function invite() {
    const username = session?.user.username;
    if (!username) return;
    try {
      const shared = await shareText(inviteText(username), inviteLink(username));
      if (!shared) {
        await navigator.clipboard.writeText(`${inviteText(username)}\n${inviteLink(username)}`);
        setError('Convite copiado. É só colar na conversa com a pessoa.');
      }
      // O convite vira conversa aberta em Conversas (aguardando a pessoa entrar).
      await recordInvite(draft.contactId);
    } catch {
      setError('Não consegui abrir o compartilhamento agora.');
    }
  }

  function dismiss() {
    markDraftHandled(activityId);
    onDismiss();
  }

  function handOff() {
    const body = text.trim();
    if (!digits || !body) return;
    hapticTap();
    // A assinatura Ⓢ vai por padrão; a pessoa desliga em "Jeito de falar".
    openWhatsApp(digits, signWhatsApp(body, profile?.preferences.whatsappSignature !== false));
    setHanded(true);
  }

  // Sec Full (só nessa edição): o botão envia de verdade pelo WhatsApp conectado do
  // dono, pela trilha que o servidor antigo já usa. Falhou ou sem conexão: abre o
  // WhatsApp com a mensagem pronta, como nas outras edições.
  async function sendWhatsAppReal() {
    const body = text.trim();
    if (!digits || !body || busy) return;
    const signed = signWhatsApp(body, profile?.preferences.whatsappSignature !== false);
    setBusy(true);
    setError('');
    hapticTap();
    // Import com a variável de ambiente crua: nos pacotes padrão o trecho é código
    // morto e o Vite descarta o cliente do servidor antigo (nada vai para a loja).
    if (import.meta.env.VITE_EDITION !== 'full') {
      setBusy(false);
      handOff();
      return;
    }
    try {
      const { legacyConnected, legacySendWhatsApp } = await import('../../lib/full/legacy.js');
      if (!legacyConnected()) {
        setBusy(false);
        handOff();
        return;
      }
      await legacySendWhatsApp(digits, signed);
      markDraftHandled(activityId);
      hapticSuccess();
      track('whatsapp_handoff', { sent: true });
      setWaSent(true);
      setBusy(false);
    } catch (sendError) {
      setBusy(false);
      setError(
        sendError instanceof Error ? sendError.message : 'Não consegui enviar pelo WhatsApp agora.',
      );
      hapticWarn();
    }
  }

  if (IS_FULL && waSent) {
    return (
      <section className="am-draft" aria-label={`Mensagem para ${draft.contactName}`}>
        <header className="am-draft-to">
          <span className="am-draft-av" aria-hidden="true">
            {initial}
          </span>
          <span>
            <small>Enviei pelo WhatsApp</small>
            <b>{draft.contactName}</b>
          </span>
          <em>entregue</em>
        </header>
        <p className="am-note">Mandei pelo seu WhatsApp. ✓</p>
        <div className="am-draft-acts">
          <button className="am-primary" onClick={onDismiss}>
            Pronto
          </button>
        </div>
      </section>
    );
  }

  if (handed) {
    return (
      <section className="am-draft" aria-label={`Mensagem para ${draft.contactName}`}>
        <header className="am-draft-to">
          <span className="am-draft-av" aria-hidden="true">
            {initial}
          </span>
          <span>
            <small>No WhatsApp</small>
            <b>{draft.contactName}</b>
          </span>
          <em>pronta para enviar</em>
        </header>
        <p className="am-note">Deixei a mensagem pronta lá. É só tocar em enviar. Mandou?</p>
        <div className="am-draft-acts">
          <button
            className="am-primary"
            onClick={() => {
              hapticSuccess();
              track('whatsapp_handoff', { sent: true });
              markDraftHandled(activityId);
              onDismiss();
            }}
          >
            Enviei
          </button>
          <button
            className="am-soft"
            onClick={() => {
              track('whatsapp_handoff', { sent: false });
              setHanded(false);
            }}
          >
            Voltar ao rascunho
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="am-draft" aria-label={`Mensagem para ${draft.contactName}`}>
      <header className="am-draft-to">
        <span className="am-draft-av" aria-hidden="true">
          {initial}
        </span>
        <span>
          <small>Para</small>
          <b>{draft.contactName}</b>
        </span>
        <em>{draft.username ? 'rascunho' : 'ainda não está no Secretário'}</em>
      </header>
      {editing ? (
        <textarea
          className="am-draft-edit"
          value={text}
          onChange={(event) => setText(event.target.value)}
          rows={3}
          maxLength={2000}
          aria-label="Texto da mensagem"
          autoFocus
        />
      ) : (
        <p className="am-draft-bubble">{text}</p>
      )}
      {error && <p className="am-note">{error}</p>}
      <div className="am-draft-acts">
        {draft.username ? (
          <button
            className="am-primary"
            onClick={() => void send()}
            disabled={busy || !text.trim()}
          >
            {busy ? 'Enviando…' : 'Enviar'}
          </button>
        ) : digits ? (
          <button
            className="am-primary"
            onClick={IS_FULL ? () => void sendWhatsAppReal() : handOff}
            disabled={busy || !text.trim()}
          >
            {IS_FULL ? (busy ? 'Enviando…' : 'Enviar pelo WhatsApp') : 'Abrir no WhatsApp'}
          </button>
        ) : (
          <button className="am-primary" onClick={() => void invite()}>
            Convidar {draft.contactName.split(' ')[0]}
          </button>
        )}
        {draft.username && digits && (
          <button
            className="am-soft"
            onClick={IS_FULL ? () => void sendWhatsAppReal() : handOff}
            disabled={busy || !text.trim()}
          >
            {IS_FULL ? 'Enviar pelo WhatsApp' : 'Abrir no WhatsApp'}
          </button>
        )}
        {!draft.username && digits && (
          <button className="am-soft" onClick={() => void invite()} disabled={busy}>
            Convidar
          </button>
        )}
        <button className="am-soft" onClick={() => setEditing((value) => !value)} disabled={busy}>
          {editing ? 'Pronto' : 'Editar'}
        </button>
        <button className="am-ghost" onClick={dismiss} disabled={busy}>
          Não enviar
        </button>
      </div>
    </section>
  );
}
